import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initSentry, captureError } from "@/lib/sentry";
import { watchForBlankScreen } from "@/lib/blankScreenWatch";

// VA-270: tells the inline boot guard in index.html that the bundle actually ran.
// Imports hoist, so this executes only once every module above has evaluated — which is
// exactly the signal wanted. It lets the guard tell "the bundle never loaded" (its job —
// reload once, then explain) from "the bundle loaded and rendered nothing"
// (blankScreenWatch's job — observe, never reload, because a reload destroys the evidence).
(window as unknown as { __vaBootStarted?: boolean }).__vaBootStarted = true;

// Sentry must be initialised before any other code so it catches early errors
initSentry();

// Temporary: expose sentryTest() so connection can be verified from console
(window as any).sentryTest = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  console.log("DSN present:", !!dsn, dsn ? dsn.slice(0, 30) + "..." : "MISSING");
  captureError(new Error("Sentry connection test — Vision Assistant"));
};

// ── Polyfill: matchMedia.addEventListener for Safari <14 ──
// Safari 13 and earlier only support the deprecated addListener/removeListener
// methods on MediaQueryList. Many libraries (Radix UI, shadcn, chart libs) call
// addEventListener('change', ...) which crashes on those browsers.
// This polyfill patches the prototype once, fixing ALL matchMedia usage globally.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  const proto = window.MediaQueryList?.prototype;
  if (proto && typeof proto.addEventListener !== "function") {
    proto.addEventListener = function (
      _type: string,
      listener: EventListenerOrEventListenerObject,
      _options?: boolean | AddEventListenerOptions
    ) {
      const handler =
        typeof listener === "function" ? listener : listener?.handleEvent?.bind(listener);
      if (handler) this.addListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    };
    proto.removeEventListener = function (
      _type: string,
      listener: EventListenerOrEventListenerObject,
      _options?: boolean | EventListenerOptions
    ) {
      const handler =
        typeof listener === "function" ? listener : listener?.handleEvent?.bind(listener);
      if (handler) this.removeListener(handler as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    };
  }
}

// Development cache buster  -  remove stale service workers in preview
if (window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1')) {
  if ('caches' in window) {
    caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
  }
}

createRoot(document.getElementById("root")!).render(<App />);

// VA-230. Reports once if nothing has rendered a few seconds after load, with the facts
// that separate "stale build" from "stuck at runtime". It only observes — see the file.
watchForBlankScreen();
